/**
 * PhaseBladeSkillEffect — Blades tree sunder-path apotheosis.
 *
 * Enters Phase state for 6 seconds. During Phase, all blade attacks
 * ignore armor completely (100% armor bypass), bypass shields entirely,
 * and deal +40% hit damage. The player's blade becomes partially
 * non-physical — a culmination of the sunder path's philosophy of
 * removing defensive barriers from the equation.
 *
 * Duration-limited toggle: auto-deactivates when the 6-second timer
 * expires. Very long cooldown tier (4000ms).
 *
 * Slot cost: 1 (per design-decisions.md — all apotheosis nodes cost 1
 * slot, same as every other active skill, overriding the spec's "2 slots"
 * text).
 *
 * This skill does not deal damage directly — it sets combat modifier
 * flags that CombatHitResolver reads when resolving blade-type hits.
 * The modifiers (armor bypass, shield bypass, damage multiplier) are
 * exposed via getters so the combat system can query them during the
 * Phase window.
 *
 * Extends ToggleSkillEffect for active/cooldown lifecycle.
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see blades-spec.md § Phase Blade
 */

import { ToggleSkillEffect } from '../../ToggleSkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface PhaseBladeDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMultiplicativeEffect(skillId: string, key: string): number;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface PhaseBladeConfig {
  readonly cooldownMs: number;
  readonly durationMs: number;
  readonly armorBypass: number;
  readonly shieldBypass: boolean;
  readonly damageMultiplier: number;
}

const DEFAULT_CONFIG: PhaseBladeConfig = {
  cooldownMs: 4000,             // very_long cooldown tier
  durationMs: 6000,             // 6 seconds of Phase state
  armorBypass: 1.0,             // 100% armor bypass
  shieldBypass: true,           // full shield penetration
  damageMultiplier: 1.4,        // +40% hit damage
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class PhaseBladeSkillEffect extends ToggleSkillEffect {
  readonly skillId = 'phase_blade';

  private readonly deps: PhaseBladeDeps;
  private readonly config: PhaseBladeConfig;

  // Active-state timer
  private _durationRemaining = 0;

  // Resolved values (set at activation, immutable for the Phase window)
  private _resolvedDurationMs = 0;
  private _resolvedDamageMultiplier = 0;

  // Counters for assertions / game-layer queries
  private _activationCount = 0;

  constructor(deps: PhaseBladeDeps, config: Partial<PhaseBladeConfig> = {}) {
    super();
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get durationRemaining(): number { return this._durationRemaining; }
  get resolvedDurationMs(): number { return this._resolvedDurationMs; }
  get activationCount(): number { return this._activationCount; }

  /**
   * Armor bypass fraction while Phase is active.
   * Returns 1.0 (100% bypass) during Phase, 0 otherwise.
   * CombatHitResolver applies: `effectiveArmor * (1 - armorBypass)`.
   */
  get armorBypass(): number {
    return this._isActive ? this.config.armorBypass : 0;
  }

  /**
   * Shield bypass flag while Phase is active.
   * Returns true during Phase (shields fully penetrated), false otherwise.
   */
  get shieldBypass(): boolean {
    return this._isActive ? this.config.shieldBypass : false;
  }

  /**
   * Damage multiplier while Phase is active.
   * Returns the resolved multiplier during Phase, 1.0 otherwise.
   * Applied multiplicatively with other blade damage modifiers.
   */
  get damageMultiplier(): number {
    return this._isActive ? this._resolvedDamageMultiplier : 1.0;
  }

  // --- ToggleSkillEffect interface ---

  canActivate(): boolean {
    return !this._isActive && this.cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Resolve damage multiplier through the upgrade resolver chain
    this._resolvedDamageMultiplier = this.config.damageMultiplier *
      this.deps.resolver.getMultiplicativeEffect('phase_blade', 'damage');

    this._resolvedDurationMs = this.config.durationMs;
    this._durationRemaining = this._resolvedDurationMs;
    this._activationCount++;

    this._isActive = true;
    return true;
  }

  protected onDeactivate(): void {
    this._durationRemaining = 0;
    this.cooldownRemaining = this.config.cooldownMs;
  }

  protected updateWhileActive(delta: number): void {
    this._durationRemaining = Math.max(0, this._durationRemaining - delta);
    if (this._durationRemaining <= 0) {
      this.deactivate();
    }
  }

  destroy(): void {
    this._durationRemaining = 0;
    super.destroy();
  }
}
